import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { useLang } from "@/i18n";
import { useCursor } from "@/context/CursorContext";
import { useTheme } from "@/context/ThemeContext";

export const Navbar = ({ onNavigate }) => {
  const { t } = useLang();
  const { setCursor, reset } = useCursor();
  const { theme, toggle: toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { key: "work", label: t.nav.work },
    { key: "about", label: t.nav.about },
    { key: "contact", label: t.nav.contact },
  ];

  const linkHover = { onMouseEnter: () => setCursor("link"), onMouseLeave: reset };

  const go = (key) => {
    setOpen(false);
    onNavigate(key);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-void/70 backdrop-blur-xl border-b border-border" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
      <nav className="mx-auto max-w-[1600px] px-6 md:px-8 lg:px-12 h-24 flex items-center justify-between gap-4">
        <button
          {...linkHover}
          onClick={() => onNavigate("top")}
          data-testid="logo-btn"
          className="flex items-center gap-3 group"
        >
          <span className="font-display text-3xl lg:text-4xl xl:text-5xl font-bold uppercase leading-[0.9] tighter text-bone group-hover:text-navy-ink transition-colors duration-300 whitespace-nowrap">
            Polat Pala
          </span>
        </button>

        <div className="hidden md:flex items-center gap-4 lg:gap-8 xl:gap-10">
          {links.map((l) => (
            <button
              key={l.key}
              {...linkHover}
              onClick={() => onNavigate(l.key)}
              data-testid={`nav-${l.key}`}
              className="font-display text-xl lg:text-2xl xl:text-4xl 2xl:text-5xl font-normal uppercase leading-[0.9] tighter text-bone/60 hover:text-bone transition-colors duration-300 whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 lg:gap-4">
          <button
            {...linkHover}
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-bone/70 hover:text-navy-ink hover:border-navy-ink transition-colors duration-300"
          >
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          <button
            {...linkHover}
            onClick={() => onNavigate("contact")}
            data-testid="nav-cta"
            className="hidden lg:inline-flex items-center rounded-full bg-navy px-5 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-white hover:bg-navy-ink transition-colors duration-300 whitespace-nowrap"
          >
            {t.nav.cta}
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="md:hidden flex h-10 w-10 flex-col items-center justify-center gap-1.5 rounded-full border border-border"
          >
            <motion.span animate={{ rotate: open ? 45 : 0, y: open ? 4 : 0 }} className="block h-px w-4 bg-bone" />
            <motion.span animate={{ rotate: open ? -45 : 0, y: open ? -3 : 0 }} className="block h-px w-4 bg-bone" />
          </button>
        </div>
      </nav>
      </motion.header>

      {/* Mobile drawer — rendered as a SIBLING of the header (not a child)
          so the header's `backdrop-blur-xl` doesn't establish a containing
          block that collapses the fixed drawer to 0 height once scrolled. */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden fixed inset-0 top-24 z-40 bg-void backdrop-blur-xl overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 pt-8">
              {links.map((l, i) => (
                <motion.button
                  key={l.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.06 }}
                  onClick={() => go(l.key)}
                  data-testid={`mobile-nav-${l.key}`}
                  className="border-b border-border py-6 text-left font-display text-4xl font-medium uppercase tracking-tight text-bone"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                onClick={() => go("contact")}
                data-testid="mobile-nav-cta"
                className="mt-10 inline-flex items-center justify-center rounded-full bg-navy px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase text-white"
              >
                {t.nav.cta}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
